import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  DollarSign,
  Eye,
  Gamepad2,
  Link as LinkIcon,
  Copy,
  CheckCircle2,
  CalendarDays,
  TrendingUp,
  Plus,
  ExternalLink,
  Trash2,
  Youtube,
  Tv,
  Twitter,
  Instagram,
  Globe,
} from "lucide-react";

function InfluencerCampaignPerformancePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [joinData, setJoinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [submittedPosts, setSubmittedPosts] = useState([]);
  const [postUrl, setPostUrl] = useState("");
  const [postPlatform, setPostPlatform] = useState("youtube");
  const [postTitle, setPostTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState("");

  useEffect(() => {
    async function fetchData() {
      if (!currentUser) return;
      try {
        // Fetch campaign
        const campSnap = await getDoc(doc(db, "campaigns", id));
        if (campSnap.exists()) {
          setCampaign({ id: campSnap.id, ...campSnap.data() });
        }

        // Fetch join data for this user + campaign
        const q = query(
          collection(db, "campaignJoins"),
          where("campaignId", "==", id),
          where("influencerId", "==", currentUser.uid)
        );
        const joinSnap = await getDocs(q);
        if (!joinSnap.empty) {
          const jd = { id: joinSnap.docs[0].id, ...joinSnap.docs[0].data() };
          setJoinData(jd);
          setSubmittedPosts(jd.submittedPosts || []);
        }
      } catch (err) {
        console.error("Failed to fetch performance data:", err);
      }
      setLoading(false);
    }
    fetchData();
  }, [id, currentUser]);

  const platformOptions = [
    { value: "youtube", label: "YouTube", icon: Youtube },
    { value: "twitch", label: "Twitch", icon: Tv },
    { value: "twitter", label: "X / Twitter", icon: Twitter },
    { value: "instagram", label: "Instagram", icon: Instagram },
    { value: "tiktok", label: "TikTok", icon: Globe },
    { value: "other", label: "Other", icon: Globe },
  ];

  async function handleSubmitPost() {
    if (!postUrl.trim()) {
      setPostError("Please enter a URL.");
      return;
    }
    try {
      new URL(postUrl.trim());
    } catch {
      setPostError("Please enter a valid URL.");
      return;
    }
    setSubmitting(true);
    setPostError("");
    try {
      const newPost = {
        url: postUrl.trim(),
        platform: postPlatform,
        title: postTitle.trim() || "",
        addedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, "campaignJoins", joinData.id), {
        submittedPosts: arrayUnion(newPost),
      });
      setSubmittedPosts((prev) => [...prev, newPost]);
      setPostUrl("");
      setPostTitle("");
      setPostPlatform("youtube");

      try {
        const sendPostSubmittedEmail = httpsCallable(functions, "sendPostSubmittedEmail");
        await sendPostSubmittedEmail({
          campaignId: joinData.campaignId || id,
          campaignTitle: joinData.campaignTitle || campaign?.title || "",
          publisherId: campaign?.publisherId || "",
          postUrl: newPost.url,
          postPlatform: newPost.platform,
        });
      } catch (emailErr) {
        console.error("Failed to send post submitted email:", emailErr);
      }
    } catch (err) {
      console.error("Failed to submit post:", err);
      setPostError("Failed to submit post. Please try again.");
    }
    setSubmitting(false);
  }

  async function handleRemovePost(index) {
    const updated = submittedPosts.filter((_, i) => i !== index);
    try {
      await updateDoc(doc(db, "campaignJoins", joinData.id), {
        submittedPosts: updated,
      });
      setSubmittedPosts(updated);
    } catch (err) {
      console.error("Failed to remove post:", err);
    }
  }

  function getPlatformIcon(platform) {
    const opt = platformOptions.find((p) => p.value === platform);
    const Icon = opt?.icon || Globe;
    return <Icon className="w-4 h-4" />;
  }

  function handleCopyLink() {
    if (joinData?.utmLink) {
      navigator.clipboard.writeText(joinData.utmLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!campaign || !joinData) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
            <p className="text-gray-400">Campaign not found or you haven't joined this campaign.</p>
          </div>
        </div>
      </div>
    );
  }

  const wishlists = joinData.wishlists || 0;
  const purchases = joinData.purchases || 0;
  const visits = joinData.visits || 0;
  const cType = joinData.campaignType || "wishlist";
  const pricePerUnit = cType === "purchase" ? (joinData.pricePerPurchase || 0) : (joinData.pricePerWishlist || 0);
  const actionCount = cType === "purchase" ? purchases : wishlists;
  const hasTierSpend = joinData.tierSpend != null;
  const earned = hasTierSpend ? joinData.tierSpend : actionCount * pricePerUnit;
  const conversionRate = visits > 0 ? ((actionCount / visits) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Campaign Header */}
        {campaign.bannerImageUrl && (
          <div className="w-full rounded-2xl overflow-hidden mb-6" style={{ aspectRatio: "16/9" }}>
            <img
              src={campaign.bannerImageUrl}
              alt={campaign.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="flex items-start gap-4 mb-8">
          {campaign.coverImageUrl && (
            <img
              src={campaign.coverImageUrl}
              alt={campaign.title}
              className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold mb-1">{campaign.title}</h1>
            <p className="text-sm text-gray-500">by {campaign.companyName}</p>
          </div>
          <span
            className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${
              campaign.status === "active"
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
            }`}
          >
            {campaign.status}
          </span>
        </div>

        {/* Performance Stats */}
        <h2 className="text-lg font-semibold mb-4">Your Performance</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <DollarSign className="w-4 h-4" />
              Earned
            </div>
            <p className="text-2xl font-bold text-green-400">${earned.toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">{hasTierSpend ? "tiered pricing" : `$${pricePerUnit} per ${cType}`}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <Gamepad2 className="w-4 h-4" />
              {cType === "purchase" ? "Purchases" : "Wishlists"}
            </div>
            <p className="text-2xl font-bold text-brand-400">{actionCount.toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">generated from your link</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <Eye className="w-4 h-4" />
              Visits
            </div>
            <p className="text-2xl font-bold">{visits.toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">total link clicks</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <TrendingUp className="w-4 h-4" />
              Conversion
            </div>
            <p className="text-2xl font-bold">{conversionRate}%</p>
            <p className="text-xs text-gray-600 mt-1">visits → {cType === "purchase" ? "purchases" : "wishlists"}</p>
          </div>
        </div>

        {/* UTM Link */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-400">Your UTM Link</h3>
          </div>
          <div className="flex items-center gap-3">
            <code className="text-sm text-brand-400 bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 break-all flex-1">
              {joinData.utmLink}
            </code>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2.5 rounded-xl transition-colors text-sm font-medium flex-shrink-0"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Campaign Details */}
        <h2 className="text-lg font-semibold mb-4">Campaign Details</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <CalendarDays className="w-4 h-4" />
              Campaign Period
            </div>
            <p className="text-sm font-semibold">
              {campaign.startDate} — {campaign.endDate}
            </p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <DollarSign className="w-4 h-4" />
              Max Budget
            </div>
            <p className="text-xl font-bold">${campaign.maxBudget?.toLocaleString()}</p>
          </div>
        </div>

        {/* Submitted Posts */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Published Content</h2>

          {/* Existing posts */}
          {submittedPosts.length > 0 ? (
            <div className="space-y-3 mb-4">
              {submittedPosts.map((post, i) => (
                <div
                  key={i}
                  className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 flex-shrink-0">
                    {getPlatformIcon(post.platform)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {post.title || post.url}
                    </p>
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-brand-400 hover:text-brand-300 truncate block"
                    >
                      {post.url}
                    </a>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {new Date(post.addedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <a
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemovePost(i)}
                    className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-6 text-center mb-4">
              <p className="text-sm text-gray-500">No content submitted yet. Add links to your published posts below.</p>
            </div>
          )}

          {/* Add post form */}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-5">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Add a Post</h3>
            <div className="space-y-3">
              {/* Platform selector */}
              <div className="flex flex-wrap gap-2">
                {platformOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPostPlatform(opt.value)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                      postPlatform === opt.value
                        ? "bg-brand-500/20 text-brand-400 border border-brand-500/30"
                        : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600"
                    }`}
                  >
                    <opt.icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* URL */}
              <input
                type="url"
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              {/* Title (optional) */}
              <input
                type="text"
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                placeholder="Post title or description (optional)"
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              {postError && (
                <p className="text-xs text-red-400">{postError}</p>
              )}
              <button
                type="button"
                onClick={handleSubmitPost}
                disabled={submitting}
                className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Submit Post
              </button>
            </div>
          </div>
        </div>

        {/* View Campaign Page button */}
        <button
          onClick={() => navigate(`/campaigns/${id}`)}
          className="w-full bg-gray-900/50 border border-gray-800/50 hover:border-gray-700/50 rounded-2xl p-4 text-center text-sm text-gray-400 hover:text-white transition-all"
        >
          View Campaign Page →
        </button>
      </div>
    </div>
  );
}

export default InfluencerCampaignPerformancePage;
